import { ArrowRight, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { inviteApi } from "../../../api/client";
import LoadingState from "../../../components/common/LoadingState";
import { useToast } from "../../../context/ToastContext";
import type { AdminInvite } from "../../../types/api";

function humanizeRole(name: string) {
  return name
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export default function AdminInviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [invite, setInvite] = useState<AdminInvite | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    inviteApi
      .get(token)
      .then(({ invite }) => setInvite(invite))
      .catch((requestError: unknown) => {
        setLoadError(
          requestError instanceof Error ? requestError.message : "Invite is no longer valid"
        );
      });
  }, [token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;

    if (!name.trim()) {
      setSubmitError("Name is required");
      return;
    }
    if (password.length < 8) {
      setSubmitError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError("Passwords don't match");
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await inviteApi.accept(token, { name: name.trim(), password });
      showToast("Account created. Sign in with your new password.", "success");
      navigate("/admin/login", { replace: true });
    } catch (requestError) {
      setSubmitError(
        requestError instanceof Error ? requestError.message : "Could not accept invite"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!invite && !loadError) return <LoadingState label="Loading invite..." />;

  return (
    <div className="ego-shell flex min-h-screen items-center justify-center px-4 py-10">
      <div className="ego-surface w-full max-w-md p-8">
        {loadError ? (
          <div className="text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] text-[#b91c1c]">
              <ShieldCheck size={18} />
            </div>
            <h1 className="text-xl font-bold text-foreground">Invite unavailable</h1>
            <p className="mt-2 text-sm text-muted-foreground">{loadError}</p>
            <button
              type="button"
              onClick={() => navigate("/admin/login")}
              className="ego-btn-secondary mt-6"
            >
              Go to admin login
            </button>
          </div>
        ) : invite ? (
          <>
            <span className="ego-badge ego-badge-brand">
              <ShieldCheck size={12} /> Admin invite
            </span>
            <h1 className="mt-3 text-2xl font-bold tracking-tight text-foreground">
              Welcome to the team
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              You've been invited as <strong>{humanizeRole(invite.role)}</strong> ·{" "}
              {invite.email}
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <label className="block">
                <span className="mb-1 block text-sm font-semibold text-foreground">Full name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(inputEvent) => setName(inputEvent.target.value)}
                  className="ego-input"
                  required
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-semibold text-foreground">Password</span>
                <input
                  type="password"
                  value={password}
                  onChange={(inputEvent) => setPassword(inputEvent.target.value)}
                  className="ego-input"
                  minLength={8}
                  required
                />
                <span className="mt-1 block text-[11px] text-[#9aa3b2]">At least 8 characters</span>
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-semibold text-foreground">
                  Confirm password
                </span>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(inputEvent) => setConfirmPassword(inputEvent.target.value)}
                  className="ego-input"
                  minLength={8}
                  required
                />
              </label>

              {submitError && (
                <div className="rounded-xl border border-[rgba(220,38,38,0.18)] bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] px-3 py-2 text-sm font-semibold text-[#b91c1c]">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="ego-btn-primary w-full !py-3"
              >
                {isSubmitting ? "Creating account..." : "Create account"}
                {!isSubmitting && <ArrowRight size={16} />}
              </button>
            </form>

            <p className="mt-5 text-center text-[11px] text-[#9aa3b2]">
              Invited by {invite.invitedByEmail || "system"} · expires{" "}
              {new Date(invite.expiresAt).toLocaleDateString()}
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
